import { useTranslation } from 'react-i18next'

import Icon from '../../../../../ui/Icon'

import { useAuth } from '../../../../../../contexts/AuthContext'
import { ProfileMetadata } from '../../../../../../types/user/userProfile'
import { hasAnyRole, RoleType } from '../../../../../../utils/jwtUtils'

type UserRolesInputProps = {
  user: ProfileMetadata
  loading?: boolean
  onRolesChange: (roles: string[]) => void
}

const ROLE_ORDER = [RoleType.USER, RoleType.MANAGER, RoleType.ADMIN]

/**
 * Compact role toggles: active roles are filled, inactive ones outlined. USER can never be removed, and admins
 * cannot remove their own ADMIN role.
 */
export const UserRolesInput = ({
  user,
  loading = false,
  onRolesChange,
}: UserRolesInputProps) => {
  const { t } = useTranslation('users')
  const { authInfo, authenticatedUserId } = useAuth()

  const roles = user.roleTypeList ?? []

  const isAuthenticatedUser =
    String(user.userId) === String(authenticatedUserId)

  const authenticatedUserIsAdmin = authInfo?.accessToken
    ? hasAnyRole(authInfo.accessToken, [RoleType.ADMIN])
    : false

  const lockedRoles: RoleType[] = [
    RoleType.USER,
    ...(isAuthenticatedUser && authenticatedUserIsAdmin
      ? [RoleType.ADMIN]
      : []),
  ]

  const toggle = (role: RoleType) => {
    onRolesChange(
      roles.includes(role)
        ? roles.filter(current => current !== role)
        : ROLE_ORDER.filter(
            current => current === role || roles.includes(current)
          )
    )
  }

  return (
    <div className="user-roles" role="group" aria-label={t('users.list.roles')}>
      {ROLE_ORDER.map(role => {
        const active = roles.includes(role)
        const locked = lockedRoles.includes(role) && active

        return (
          <button
            key={role}
            type="button"
            className={`user-role${active ? ' active' : ''}${locked ? ' locked' : ''}`}
            aria-pressed={active}
            disabled={loading || locked}
            title={locked ? t('users.roles.locked') : undefined}
            onClick={() => toggle(role)}
          >
            {locked && <Icon name="lock" size={11} />}
            {t(`users.roles.${role.toLowerCase()}`)}
          </button>
        )
      })}
    </div>
  )
}
